import React, { useEffect, useState } from 'react';
import {
  Button,
  Grid,
  TextField,
  CircularProgress,
  InputAdornment,
  Alert,
  Box,
  Divider,
  Typography,
  Autocomplete,
} from '@mui/material';
import RefreshIcon from '@mui/icons-material/Refresh';
import SaveTwoToneIcon from '@mui/icons-material/SaveTwoTone';
import intlFormat from 'date-fns/intlFormat';
import PropTypes from 'prop-types';

import axiosInstance from '../../api';
import SERVER_API_URL from '../../config';
import { NAME_REGEX, GENDER_REGEX, PHONE_REGEX } from '../../utils/constants';
import {
  GENDER_EXCEPTION,
  INTERNAL_SERVER_ERROR,
  NAME_EXCEPTION,
  PHONE_EXCEPTION,
  REQUIRED_FIELD,
} from '../../utils/exception';

export default function EditGuarantor({ userId, guarantorToBeUpdated, handleRefreshUserData }) {
  const [firstName, setFirstName] = useState(guarantorToBeUpdated && guarantorToBeUpdated.firstName);
  const [lastName, setLastName] = useState(guarantorToBeUpdated && guarantorToBeUpdated.lastName);
  const [job, setJob] = useState(guarantorToBeUpdated && guarantorToBeUpdated.job);
  const [phone, setPhone] = useState(guarantorToBeUpdated && guarantorToBeUpdated.phone.slice(4, 13));
  const [gender, setGender] = useState(guarantorToBeUpdated && guarantorToBeUpdated.gender);
  const [categoryName, setCategoryName] = useState(guarantorToBeUpdated && guarantorToBeUpdated.category.name);
  const [churchName, setChurchName] = useState(guarantorToBeUpdated && guarantorToBeUpdated.church.name);
  const [districtName, setDistrictName] = useState(guarantorToBeUpdated && guarantorToBeUpdated.church.district.name);

  const [disabled, setDisabled] = useState(false);
  const [firstDisabled, setFirstDisabled] = useState(true);

  const [formError, setFormError] = useState(null);
  const [formErrorSeverity, setFormErrorSeverity] = useState('error');

  const [firstNameClicked, setFirstNameClicked] = useState(false);
  const [formErrorOnFirstName, setFormErrorOnFirstName] = useState(false);
  const [helperTextOnFirstName, setHelperTextOnFirstName] = useState(null);

  const [lastNameClicked, setLastNameClicked] = useState(false);
  const [formErrorOnLastName, setFormErrorOnLastName] = useState(false);
  const [helperTextOnLastName, setHelperTextOnLastName] = useState(null);

  const [jobClicked, setJobClicked] = useState(false);
  const [formErrorOnJob, setFormErrorOnJob] = useState(false);
  const [helperTextOnJob, setHelperTextOnJob] = useState(null);

  const [phoneClicked, setPhoneClicked] = useState(false);
  const [formErrorOnPhone, setFormErrorOnPhone] = useState(false);
  const [helperTextOnPhone, setHelperTextOnPhone] = useState(null);

  const [genderClicked, setGenderClicked] = useState(false);
  const [formErrorOnGender, setFormErrorOnGender] = useState(false);
  const [helperTextOnGender, setHelperTextOnGender] = useState(null);

  const [categories, setCategories] = useState(null);
  const [categoryNameClicked, setCategoryNameClicked] = useState(false);
  const [formErrorOnCategoryName, setFormErrorOnCategoryName] = useState(false);
  const [helperTextOnCategoryName, setHelperTextOnCategoryName] = useState(null);

  const [districts, setDistricts] = useState(null);
  const [districtNameClicked, setDistrictNameClicked] = useState(false);
  const [formErrorOnDistrictName, setFormErrorOnDistrictName] = useState(false);
  const [helperTextOnDistrictName, setHelperTextOnDistrictName] = useState(null);

  const [church, setChurch] = useState(null);
  const [churchNameClicked, setChurchNameClicked] = useState(false);
  const [formErrorOnChurchName, setFormErrorOnChurchName] = useState(false);
  const [helperTextOnChurchName, setHelperTextOnChurchName] = useState(null);

  const handleSubmit = async (event) => {
    event.preventDefault();
    setFormErrorSeverity('error');
    setDisabled(true);
    if (
      !formErrorOnFirstName &&
      !formErrorOnLastName &&
      !formErrorOnJob &&
      !formErrorOnPhone &&
      !formErrorOnGender &&
      !formErrorOnCategoryName &&
      !formErrorOnChurchName
    ) {
      if (guarantorToBeUpdated) {
        try {
          const response = await axiosInstance.put(
            `${SERVER_API_URL}/user/updateUserGuarantorById/${guarantorToBeUpdated && guarantorToBeUpdated.id}`,
            {
              firstName,
              lastName,
              job,
              phone,
              gender,
              categoryName,
              churchName,
              districtName,
            }
          );

          if (response.status === 200) {
            setFormError('የዋስትና መረጃ በተሳካ ሁኔታ ተቀምጧል።');
            setFormErrorSeverity('success');
          }
        } catch (e) {
          if (e.response.status === 406) {
            setFormError(e.response.data.message);
          } else if (e.response.status === 404) {
            setFormError(`የተጠቃሚው ዋስ አልተገኘም።`);
          } else if (e.response.status === 500) {
            setFormError(INTERNAL_SERVER_ERROR);
          }
        }
      } else {
        try {
          const response = await axiosInstance.post(`${SERVER_API_URL}/user/userGuarantorRegister/${userId}`, {
            firstName,
            lastName,
            job,
            phone,
            gender,
            categoryName,
            churchName,
            districtName,
          });
          if (response.status === 201) {
            setFormError('ዋስትና በተሳካ ሁኔታ ተመዝግቧል');
            setFormErrorSeverity('success');
          }
        } catch (e) {
          if (e.response.status === 406) {
            setFormError(e.response.data.message);
          } else if (e.response.status === 404) {
            setFormError(`ተጠቃሚ አልተገኘም።`);
          } else if (e.response.status === 500) {
            setFormError(INTERNAL_SERVER_ERROR);
          }
        }
      }
    } else {
      setFormError('እባክዎትን አስፈላጊ የሆኑትን መስኮች በትክክል ሙሉ!');
    }

    setDisabled(false);
    setFirstDisabled(true);
  };

  const getChurch = async (districtName) => {
    const selectedDistrict = districts.find((district) => district.name === districtName);
    try {
      const response = await axiosInstance.get(`${SERVER_API_URL}/church/findManyByDistrictId/${selectedDistrict.id}`);
      if (response.status === 200) {
        if (response.data.length > 0) {
          setChurch(response.data);
        } else {
          setChurch(null);
        }
      }
    } catch (error) {
      // console.log(error);
    }
  };

  useEffect(() => {
    const getDistricts = async () => {
      try {
        const response = await axiosInstance.get(`${SERVER_API_URL}/district/findEvery`);
        if (response.status === 200) {
          // console.log(response.data)
          setDistricts(response.data);
        }
      } catch (error) {
        // console.log(error);
      }
    };
    const getCategories = async () => {
      try {
        const response = await axiosInstance.get(`${SERVER_API_URL}/category/findAll`);
        if (response.status === 200) {
          setCategories(response.data);
        }
      } catch (error) {
        // console.log(error);
      }
    };
    getDistricts();
    getCategories();
  }, [setDistricts, setCategories]);

  const handleFormValueBlur = (e) => {
    e.preventDefault();
    setFormError(null);
    setFormErrorSeverity('error');
    setFirstDisabled(false);

    const { name, value } = e.target;
    switch (name) {
      case 'firstName':
        if (value.length === 0) {
          setFirstNameClicked(false);
          setFormErrorOnFirstName(firstNameClicked && value.length === 0);
          setHelperTextOnFirstName(REQUIRED_FIELD);
        } else if (!NAME_REGEX.test(value)) {
          setFormErrorOnFirstName(true);
          setHelperTextOnFirstName(NAME_EXCEPTION);
        } else {
          setHelperTextOnFirstName(null);
          setFormErrorOnFirstName(false);
        }
        break;
      case 'lastName':
        if (value.length === 0) {
          setLastNameClicked(false);
          setFormErrorOnLastName(lastNameClicked && value.length === 0);
          setHelperTextOnLastName(REQUIRED_FIELD);
        } else if (!NAME_REGEX.test(value)) {
          setFormErrorOnLastName(true);
          setHelperTextOnLastName(NAME_EXCEPTION);
        } else {
          setHelperTextOnLastName(null);
          setFormErrorOnLastName(false);
        }
        break;
      case 'job':
        if (value.length === 0) {
          setJobClicked(false);
          setFormErrorOnJob(jobClicked && value.length === 0);
          setHelperTextOnJob(REQUIRED_FIELD);
        } else if (!NAME_REGEX.test(value)) {
          setFormErrorOnJob(true);
          setHelperTextOnJob(NAME_EXCEPTION);
        } else {
          setHelperTextOnJob(null);
          setFormErrorOnJob(false);
        }
        break;
      case 'phone':
        if (value.length === 0) {
          setPhoneClicked(false);
          setFormErrorOnPhone(phoneClicked && value.length === 0);
          setHelperTextOnPhone(REQUIRED_FIELD);
        } else if (!PHONE_REGEX.test(value)) {
          setFormErrorOnPhone(true);
          setHelperTextOnPhone(PHONE_EXCEPTION);
        } else {
          setHelperTextOnPhone(null);
          setFormErrorOnPhone(false);
        }
        break;
      case 'gender':
        setGender(value);
        if (value.length === 0) {
          setGenderClicked(false);
          setFormErrorOnGender(genderClicked && value.length === 0);
          setHelperTextOnGender(REQUIRED_FIELD);
        } else if (!GENDER_REGEX.test(value)) {
          setFormErrorOnGender(true);
          setHelperTextOnGender(GENDER_EXCEPTION);
        } else {
          setHelperTextOnGender(null);
          setFormErrorOnGender(false);
        }
        break;
      case 'categoryName':
        setCategoryName(value);
        if (value.length === 0) {
          setCategoryNameClicked(false);
          setFormErrorOnCategoryName(categoryNameClicked && value.length === 0);
          setHelperTextOnCategoryName(REQUIRED_FIELD);
        } else if (!NAME_REGEX.test(value)) {
          setFormErrorOnCategoryName(true);
          setHelperTextOnCategoryName(NAME_EXCEPTION);
        } else if (!categories || !categories.some((obj) => obj.name === value)) {
          setFormErrorOnCategoryName(true);
          setHelperTextOnCategoryName('የምድብ ስም አስቀድሞ ከተመዘገቡ ምድቦች ውስጥ አንዱ መሆን አለበት።');
        } else {
          setHelperTextOnCategoryName(null);
          setFormErrorOnCategoryName(false);
        }
        break;
      case 'districtName':
        setDistrictName(value);
        if (value.length === 0) {
          setDistrictNameClicked(false);
          setFormErrorOnDistrictName(districtNameClicked && value.length === 0);
          setHelperTextOnDistrictName(REQUIRED_FIELD);
          setFormErrorOnChurchName(true);
          setChurch(null);
        } else if (!NAME_REGEX.test(value)) {
          setChurch(null);
          setFormErrorOnDistrictName(true);
          setHelperTextOnDistrictName(NAME_EXCEPTION);
        } else if (!districts || !districts.some((obj) => obj.name === value)) {
          setChurch(null);
          setFormErrorOnDistrictName(true);
          setHelperTextOnDistrictName('የአውራጃ ስም ቀድሞ ከተመዘገቡ አዉራጃዎች ውስጥ አንዱ መሆን አለበት።');
          setFormErrorOnChurchName(true);
        } else {
          getChurch(value);
          setHelperTextOnDistrictName(null);
          setFormErrorOnDistrictName(false);
          setFormErrorOnChurchName(false);
        }
        break;
      case 'churchName':
        setChurchName(value);
        if (value.length === 0) {
          setChurchNameClicked(false);
          setFormErrorOnChurchName(churchNameClicked && value.length === 0);
          setHelperTextOnChurchName(REQUIRED_FIELD);
        } else if (!NAME_REGEX.test(value)) {
          setFormErrorOnChurchName(true);
          setHelperTextOnChurchName(NAME_EXCEPTION);
        } else if (!church || !church.some((obj) => obj.name === value)) {
          setFormErrorOnChurchName(true);
          setHelperTextOnChurchName('የአጥቢያ ስም አስቀድሞ ከተመዘገበ አጥቢያ አንዱ መሆን አለበት።');
        } else {
          setHelperTextOnChurchName(null);
          setFormErrorOnChurchName(false);
        }
        break;
      default:
        break;
    }
  };

  const handleFormValueChange = (e) => {
    e.preventDefault();
    setFormError(null);
    setFormErrorSeverity('error');

    const { name, value } = e.target;
    switch (name) {
      case 'firstName':
        setFirstName(value);
        setFormErrorOnFirstName(false);
        setHelperTextOnLastName(null);
        break;
      case 'lastName':
        setLastName(value);
        setFormErrorOnLastName(false);
        setHelperTextOnLastName(null);
        break;
      case 'job':
        setJob(value);
        setFormErrorOnJob(false);
        setHelperTextOnJob(null);
        break;
      case 'phone':
        setPhone(value);
        setFormErrorOnPhone(false);
        setHelperTextOnPhone(null);
        break;
      case 'gender':
        setGender(value);
        setFormErrorOnGender(false);
        setHelperTextOnGender(null);
        break;
      case 'categoryName':
        setCategoryName(value);
        setFormErrorOnCategoryName(false);
        setHelperTextOnCategoryName(null);
        break;
      case 'districtName':
        setDistrictName(value);
        if (districts || districts.some((obj) => obj.name === value)) {
          getChurch(value);
        } else {
          setChurch(null);
        }
        setHelperTextOnDistrictName(null);
        setFormErrorOnDistrictName(false);
        break;
      case 'churchName':
        setChurchName(value);
        setFormErrorOnChurchName(false);
        setHelperTextOnChurchName(null);
        break;
      default:
        break;
    }
  };

  const handleClearForm = () => {
    handleRefreshUserData();

    setFormError(null);
    setFormErrorSeverity('error');
    setDisabled(false);
    setFirstDisabled(true);

    setFirstName(guarantorToBeUpdated && guarantorToBeUpdated.firstName);
    setLastName(guarantorToBeUpdated && guarantorToBeUpdated.lastName);
    setJob(guarantorToBeUpdated && guarantorToBeUpdated.job);
    setPhone(guarantorToBeUpdated && guarantorToBeUpdated.phone.slice(4, 13));
    setGender(guarantorToBeUpdated && guarantorToBeUpdated.gender);
    setCategoryName(guarantorToBeUpdated && guarantorToBeUpdated.category.name);
    setDistrictName(guarantorToBeUpdated && guarantorToBeUpdated.church.district.name);
    setChurchName(guarantorToBeUpdated && guarantorToBeUpdated.church.name);

    setFirstNameClicked(false);
    setFormErrorOnFirstName(false);
    setHelperTextOnFirstName(null);

    setLastNameClicked(false);
    setFormErrorOnLastName(false);
    setHelperTextOnLastName(null);

    setJobClicked(false);
    setFormErrorOnJob(false);
    setHelperTextOnJob(null);

    setPhoneClicked(false);
    setFormErrorOnPhone(false);
    setHelperTextOnPhone(null);

    setGenderClicked(false);
    setFormErrorOnGender(false);
    setHelperTextOnGender(null);

    setCategoryNameClicked(false);
    setFormErrorOnCategoryName(false);
    setHelperTextOnCategoryName(null);

    setDistrictNameClicked(false);
    setFormErrorOnDistrictName(false);
    setHelperTextOnDistrictName(null);

    setChurchNameClicked(false);
    setFormErrorOnChurchName(false);
    setHelperTextOnChurchName(null);
  };

  return (
    <Box sx={{ padding: 1 }} component="form" autoComplete="off" onSubmit={handleSubmit}>
      <Grid container spacing={3}>
        <Grid item xs={12} sm={7}>
          <Box sx={{ display: 'flex' }}>
            <Typography variant="h6" sx={{ color: 'darkgray', ml: 2 }}>
              የዋስትና ተጠሪ አርትዕ
            </Typography>
          </Box>
        </Grid>
        {guarantorToBeUpdated && (
          <Grid item xs={12} sm={5}>
            <Box sx={{ textAlign: 'right' }}>
              <Typography variant="body2" sx={{ color: 'text.secondary' }} noWrap>
                {guarantorToBeUpdated &&
                  intlFormat(Date.parse(guarantorToBeUpdated.updatedAt), {
                    year: 'numeric',
                    month: 'numeric',
                    day: 'numeric',
                    hour: 'numeric',
                    minute: 'numeric',
                  })}{' '}
                : ለመጨረሻ ጊዜ የተስተካከለበት ቀን
              </Typography>
              <Typography variant="body2" sx={{ color: 'text.secondary' }} noWrap>
                {guarantorToBeUpdated &&
                  intlFormat(Date.parse(guarantorToBeUpdated.createdAt), {
                    year: 'numeric',
                    month: 'numeric',
                    day: 'numeric',
                    hour: 'numeric',
                    minute: 'numeric',
                  })}{' '}
                : የተመዘገበበት ቀን
              </Typography>
            </Box>
          </Grid>
        )}
      </Grid>

      <Divider sx={{ mt: 2, mb: 2, borderStyle: 'dashed' }} />

      <Grid container spacing={3}>
        <Grid item xs={12} sm={12}>
          {formError && (
            <Alert sx={{ width: '100%' }} severity={formErrorSeverity}>
              {formError}
            </Alert>
          )}
        </Grid>
        <Grid item xs={12} sm={4}>
          <TextField
            required
            error={formErrorOnFirstName}
            id="firstName"
            name="firstName"
            label="የመጀመሪያ ስም"
            fullWidth
            // variant="standard"
            value={firstName}
            onChange={handleFormValueChange}
            helperText={formErrorOnFirstName ? helperTextOnFirstName : null}
            onFocus={() => setFirstNameClicked(true)}
            onBlur={handleFormValueBlur}
          />
        </Grid>
        <Grid item xs={12} sm={4}>
          <TextField
            required
            error={formErrorOnLastName}
            id="lastName"
            name="lastName"
            label="የአባት ስም"
            fullWidth
            // variant="standard"
            value={lastName}
            onChange={handleFormValueChange}
            helperText={formErrorOnLastName ? helperTextOnLastName : null}
            onFocus={() => setLastNameClicked(true)}
            onBlur={handleFormValueBlur}
          />
        </Grid>
        <Grid item xs={12} sm={4}>
          <TextField
            required
            error={formErrorOnJob}
            id="job"
            name="job"
            label="ስራ"
            fullWidth
            // variant="standard"
            value={job}
            onChange={handleFormValueChange}
            helperText={formErrorOnJob ? helperTextOnJob : null}
            onFocus={() => setJobClicked(true)}
            onBlur={handleFormValueBlur}
          />
        </Grid>
        <Grid item xs={12} sm={4}>
          <TextField
            required
            error={formErrorOnPhone}
            id="phone"
            name="phone"
            label="የስልክ ቁጥር"
            fullWidth
            // variant="standard"
            InputProps={{
              startAdornment: <InputAdornment job="start">+251</InputAdornment>,
            }}
            value={phone}
            onChange={handleFormValueChange}
            helperText={formErrorOnPhone ? helperTextOnPhone : null}
            onFocus={() => setPhoneClicked(true)}
            onBlur={handleFormValueBlur}
          />
        </Grid>

        <Grid item xs={12} sm={4}>
          <Autocomplete
            freeSolo
            options={['ወንድ', 'ሴት']}
            value={gender}
            renderInput={(params) => (
              <TextField
                {...params}
                required
                error={formErrorOnGender}
                id="gender"
                name="gender"
                label="ጾታ"
                // variant="standard"
                value={gender}
                onChange={handleFormValueChange}
                helperText={formErrorOnGender ? helperTextOnGender : null}
                onFocus={() => setGenderClicked(true)}
                onBlur={handleFormValueBlur}
              />
            )}
          />
        </Grid>
        <Grid item xs={12} sm={4}>
          <Autocomplete
            freeSolo
            options={
              categories && categories.length >= 1
                ? categories.map((category) => category.name)
                : ['መጀመሪያ ምድቦችን መመዝገብ አለብዎት']
            }
            value={categoryName}
            renderInput={(params) => (
              <TextField
                {...params}
                required
                error={formErrorOnCategoryName}
                id="categoryName"
                name="categoryName"
                label="የምድብ ስም"
                // variant="standard"
                onChange={handleFormValueChange}
                helperText={formErrorOnCategoryName ? helperTextOnCategoryName : null}
                onFocus={() => setCategoryNameClicked(true)}
                onBlur={handleFormValueBlur}
              />
            )}
          />
        </Grid>
        <Grid item xs={12} sm={4}>
          <Autocomplete
            freeSolo
            options={
              districts && districts.length >= 1
                ? districts.map((district) => district.name)
                : ['መጀመሪያ አንዳንድ ወረዳዎችን መመዝገብ አለቦት።']
            }
            value={districtName}
            renderInput={(params) => (
              <TextField
                {...params}
                required
                error={formErrorOnDistrictName}
                id="districtName"
                name="districtName"
                label="ወረዳ"
                // variant="standard"
                value={districtName}
                onChange={handleFormValueChange}
                helperText={formErrorOnDistrictName ? helperTextOnDistrictName : null}
                onFocus={() => setDistrictNameClicked(true)}
                onBlur={handleFormValueBlur}
              />
            )}
          />
        </Grid>
        {church && (
          <Grid item xs={12} sm={4}>
            <Autocomplete
              freeSolo
              options={
                church && church.length >= 1
                  ? church.map((oneChurch) => oneChurch.name)
                  : guarantorToBeUpdated ? guarantorToBeUpdated.church.name : ['']
              }
              value={churchName && churchName}
              renderInput={(params) => (
                <TextField
                  {...params}
                  required
                  error={formErrorOnChurchName}
                  id="churchName"
                  name="churchName"
                  label="አጥቢያ"
                  // variant="standard"
                  value={churchName && churchName}
                  onChange={handleFormValueChange}
                  helperText={formErrorOnChurchName ? helperTextOnChurchName : null}
                  onFocus={() => setChurchNameClicked(true)}
                  onBlur={handleFormValueBlur}
                />
              )}
            />
          </Grid>
        )}
        {!church && (
          <Grid item xs={12} sm={4}>
            <TextField
              fullWidth
              disabled
              label={guarantorToBeUpdated && guarantorToBeUpdated.church.name}
            // variant="standard"
            />
          </Grid>
        )}

        <Grid item xs={12} sm={12} sx={{ display: 'flex', justifyContent: 'right', mt: 5 }}>
          <Button
            color="error"
            type="reset"
            onClick={handleClearForm}
            sx={{ marginLeft: { xs: 2, sm: 5 }, minWidth: { xs: 80, sm: 150 } }}
          >
            <RefreshIcon />
            ቅጽ ዳግም አስጀምር
          </Button>{' '}
          {disabled && (
            <Button variant="contained" type="submit" sx={{ minWidth: { xs: 80, sm: 150 } }} disabled={disabled}>
              <CircularProgress style={{ width: '20px', height: '20px' }} />
              በማስቀመጥ ላይ ...
            </Button>
          )}
          {!disabled && (
            <Button
              variant="contained"
              type="submit"
              sx={{ marginLeft: { xs: 2, sm: 5 }, minWidth: { xs: 80, sm: 150 } }}
              startIcon={<SaveTwoToneIcon />}
              disabled={disabled || firstDisabled}
            >
              ለውጦችን አስቀምጥ
            </Button>
          )}
        </Grid>
      </Grid>
    </Box>
  );
}

EditGuarantor.propTypes = {
  userId: PropTypes.string,
  guarantorToBeUpdated: PropTypes.object,
  handleRefreshUserData: PropTypes.func,
};
